import React, { useState, useEffect } from "react";
import { SchoolConfig } from "../types/school";
import { AlertCircle, Loader2, Mail, Lock, Eye, EyeOff } from "lucide-react";

// Declare Google Identity Services global
declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (element: HTMLElement, options: any) => void;
          prompt: () => void;
        };
      };
    };
  }
}

interface LoginPanelProps {
  config: SchoolConfig;
  isLoading: boolean;
  error: string | null;
  onAuthSuccess: (authData: any) => void;
  onAuthError: (error: Error) => void;
  onAuthStart: () => void;
}

const LoginPanel: React.FC<LoginPanelProps> = ({
  config,
  isLoading,
  error,
  onAuthSuccess,
  onAuthError,
  onAuthStart,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"google" | "email">("google");

  // Load Google Identity Services script
  const loadGoogleScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (window.google) {
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Google script"));
      document.head.appendChild(script);
    });
  };

  // Google OAuth login handler
  const handleGoogleLogin = async () => {
    console.log("🔗 Direct Google OAuth Login Started");
    setGoogleLoading(true);
    onAuthStart();

    try {
      // Load Google Identity Services if not already loaded
      if (!window.google) {
        await loadGoogleScript();
      }

      // Configure Google Sign-In
      window.google.accounts.id.initialize({
        client_id: config.googleClientId,
        callback: handleGoogleResponse,
      });

      // Trigger the Google Sign-In flow
      window.google.accounts.id.prompt();
    } catch (error: any) {
      console.error("❌ Google OAuth initialization error:", error);
      setGoogleLoading(false);
      onAuthError(new Error("Failed to initialize Google Sign-In"));
    }
  };

  // Handle Google Sign-In response
  const handleGoogleResponse = async (response: any) => {
    try {
      console.log("🔗 Google OAuth response received");

      // Send Google ID token to backend
      const backendResponse = await fetch(
        `${config.apiBaseUrl}/api/v1/auth/google/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Tenant-Key": config.tenantId,
          },
          body: JSON.stringify({
            idToken: response.credential,
          }),
        },
      );

      if (!backendResponse.ok) {
        const error = await backendResponse.json();
        throw new Error(error.message || "Google login failed");
      }

      const authResult = await backendResponse.json();
      console.log("✅ Backend Google login successful:", authResult);

      onAuthSuccess(authResult);
    } catch (error: any) {
      console.error("❌ Google OAuth login error:", error);

      // Handle specific error types
      if (error.message.includes("TENANT_NOT_FOUND")) {
        onAuthError(
          new Error("This Google account is not registered with this school."),
        );
      } else if (
        error.message.includes("USER_NOT_FOUND") ||
        error.message.includes("INVALID_CREDENTIALS")
      ) {
        onAuthError(
          new Error(
            "This Google account is not found in the school database. Please contact your school administrator.",
          ),
        );
      } else if (error.message.includes("USER_SUSPENDED")) {
        onAuthError(
          new Error(
            "Your account is not active. Please contact your school administrator.",
          ),
        );
      } else if (error.message.includes("ROLE_NOT_ALLOWED")) {
        onAuthError(
          new Error(
            "Google sign-in is not available for your role. Please use email/password login.",
          ),
        );
      } else {
        onAuthError(error);
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  // Email login handler
  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password || isLoading) return;

    console.log("📧 Email/Password login started");
    onAuthStart();

    try {
      const response = await fetch(`${config.apiBaseUrl}/api/v1/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Tenant-Key": config.tenantId,
        },
        body: JSON.stringify({
          username: email,
          password: password,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Login failed");
      }

      const authResult = await response.json();
      console.log("✅ Email login successful:", authResult);

      onAuthSuccess(authResult);
    } catch (error: any) {
      console.error("❌ Email login error:", error);
      onAuthError(error);
    }
  };

  // Check for URL parameters on component mount (authentication redirect handling)
  useEffect(() => {
    const checkAuthParameters = async () => {
      try {
        console.log("🔍 Checking for authentication parameters...");
        console.log("🔍 Current URL:", window.location.href);
        console.log("🔍 URL Search Params:", window.location.search);

        // Check for URL parameters from backend auth success
        const urlParams = new URLSearchParams(window.location.search);
        const urlToken = urlParams.get("token");
        const urlRole = urlParams.get("role");
        const urlTenantKey = urlParams.get("tenantKey");
        const urlUsername = urlParams.get("username");

        console.log("🔍 URL Auth Parameters:", {
          urlToken: !!urlToken,
          urlTokenLength: urlToken?.length,
          urlRole,
          urlTenantKey,
          urlUsername,
        });

        // If we have complete auth parameters, redirect to main app
        if (urlToken && urlRole && urlTenantKey && urlUsername) {
          console.log(
            "✅ Complete auth parameters found - redirecting to main app",
          );

          // Redirect to the main app with auth parameters
          const redirectUrl = `${config.redirectUrl}/login?${window.location.search.substring(1)}`;
          console.log("🔄 Redirecting to:", redirectUrl);
          window.location.href = redirectUrl;
          return;
        }

        console.log("ℹ️  No auth parameters found - showing login form");
      } catch (error: any) {
        console.error("❌ Auth parameter check error:", error);
        onAuthError(new Error(error.message || "Authentication check failed"));
      }
    };

    checkAuthParameters();
  }, [config.redirectUrl, onAuthError]);

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-start space-x-3 shadow-sm">
          <div className="p-1.5 bg-red-100 rounded-lg">
            <AlertCircle className="w-4 h-4 text-red-500" />
          </div>
          <div className="flex-1">
            <p className="text-red-800 text-sm font-semibold mb-1">
              Authentication Error
            </p>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Tab Header */}
      <div className="flex mb-6 bg-slate-100 rounded-xl p-1 shadow-inner">
        <button
          onClick={() => setActiveTab("google")}
          className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all duration-300 ${
            activeTab === "google"
              ? "bg-white text-slate-900 shadow-lg transform scale-[1.02]"
              : "text-slate-500 hover:text-slate-700 hover:bg-white/50"
          }`}
        >
          <div className="flex items-center justify-center space-x-2">
            <svg className="w-4 h-4" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            <span>Google</span>
          </div>
        </button>
        <button
          onClick={() => setActiveTab("email")}
          className={`flex-1 py-2 px-3 rounded-lg text-sm font-semibold transition-all duration-300 ${
            activeTab === "email"
              ? "bg-white text-slate-900 shadow-lg transform scale-[1.02]"
              : "text-slate-500 hover:text-slate-700 hover:bg-white/50"
          }`}
        >
          <div className="flex items-center justify-center space-x-2">
            <Mail className="w-4 h-4" />
            <span>Email Login</span>
          </div>
        </button>
      </div>

      {/* Tab Content */}
      <div className="min-h-[260px]">
        {/* Google Login Tab */}
        {activeTab === "google" && (
          <div className="space-y-4">
            <div className="text-center py-4">
              <div className="mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-xl">
                  <svg className="w-10 h-10 text-white" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                </div>
                <p className="text-slate-600 font-medium mb-1">
                  Sign in with Google
                </p>
                <p className="text-slate-500 text-xs">
                  Quick and secure access with your Google account
                </p>
              </div>
            </div>

            <button
              onClick={handleGoogleLogin}
              disabled={googleLoading || isLoading}
              className="w-full flex items-center justify-center gap-3 bg-white border-2 border-slate-200 hover:border-indigo-500 py-4 rounded-2xl font-semibold transition-all hover:shadow-lg hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none group"
            >
              {googleLoading ? (
                <Loader2 className="w-5 h-5 animate-spin text-slate-600" />
              ) : (
                <img
                  src="https://www.svgrepo.com/show/475656/google-color.svg"
                  className="w-5 group-hover:scale-110 transition-transform"
                  alt="google"
                />
              )}
              <span className="text-slate-700">
                {googleLoading ? "Signing in..." : "Continue with Google"}
              </span>
            </button>

            {/* Security Notice */}
            <div className="text-center mt-6">
              <p className="text-xs text-slate-500 leading-relaxed">
                By signing in, you agree to our terms of service and privacy
                policy. Your account is protected with enterprise-grade
                security.
              </p>
            </div>
          </div>
        )}

        {/* Email Login Tab */}
        {activeTab === "email" && (
          <form onSubmit={handleEmailLogin} className="space-y-5">
            {/* Username */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Email or Username
              </label>

              <div className="relative">
                <Mail className="absolute left-4 top-4 text-slate-400 w-5" />

                <input
                  type="text"
                  placeholder="Enter your username"
                  className="w-full pl-12 pr-4 py-4 rounded-2xl border border-slate-300 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Password */}
            <div className="mb-8">
              <label className="text-sm font-medium text-slate-700 mb-2 block">
                Password
              </label>

              <div className="relative">
                <Lock className="absolute left-4 top-4 text-slate-400 w-5" />

                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter password"
                  className="w-full pl-12 pr-12 py-4 rounded-2xl border border-slate-300 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                />

                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50"
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Login Button */}
            <button
              type="submit"
              disabled={isLoading || !email || !password}
              className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white py-4 rounded-2xl font-semibold shadow-xl hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Signing In...</span>
                </div>
              ) : (
                "Sign In"
              )}
            </button>
          </form>
        )}
      </div>

      {/* Footer */}
      <p className="text-center text-slate-500 mt-6 text-xs">
        Secure access for Students, Teachers and Parents
      </p>

      {/* Help Links */}
      <div className="text-center mt-4">
        <div className="flex items-center justify-center space-x-3 text-xs">
          <a
            href="#"
            className="text-slate-500 hover:text-slate-700 transition-colors font-medium"
          >
            Need Help?
          </a>
          <span className="text-slate-300">|</span>
          <a
            href="#"
            className="text-slate-500 hover:text-slate-700 transition-colors font-medium"
          >
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
};

export default LoginPanel;
